/**
 * Enhanced breadcrumb component with schema markup for EU pages
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocation } from 'react-router-dom';

interface BreadcrumbItem {
  label: string;
  href: string;
  isActive?: boolean;
}

interface EUBreadcrumbsProps {
  customItems?: BreadcrumbItem[];
}

const EUBreadcrumbs: React.FC<EUBreadcrumbsProps> = ({ customItems }) => {
  const { currentLanguage, getLocalizedPath } = useLanguage();
  const location = useLocation();
  
  // Generate breadcrumbs based on URL path
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    if (customItems) return customItems;
    
    const pathParts = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [];
    
    // Home
    breadcrumbs.push({
      label: currentLanguage === 'de' ? 'Startseite' : 
             currentLanguage === 'fr' ? 'Accueil' :
             currentLanguage === 'it' ? 'Home' : 'Home',
      href: `/${currentLanguage}`
    });
    
    // Services
    breadcrumbs.push({
      label: 'Services',
      href: `/${currentLanguage}/services`
    });
    
    // Service type (if available)
    if (pathParts.length > 2) {
      const serviceMap: Record<string, string> = {
        'seo': 'SEO',
        'ai-video': currentLanguage === 'de' ? 'KI-Videos' : 'AI Videos',
        'webdesign': 'Webdesign'
      };
      
      const servicePath = pathParts[1];
      if (serviceMap[servicePath]) {
        breadcrumbs.push({
          label: serviceMap[servicePath],
          href: `/${currentLanguage}/${servicePath}`
        });
      }
    }
    
    // City (current page)
    if (pathParts.length > 0) {
      const citySlug = pathParts[pathParts.length - 1];
      const cityNames: Record<string, string> = {
        'stuttgart': 'Stuttgart',
        'muenchen': 'München', 'munich': 'Munich', 'monaco': 'Monaco',
        'lyon': 'Lyon', 'lione': 'Lione',
        'paris': 'Paris', 'parigi': 'Parigi',
        'mailand': 'Mailand', 'milan': 'Milan', 'milano': 'Milano',
        'como': 'Como', 'come': 'Côme'
      };
      
      if (cityNames[citySlug]) {
        breadcrumbs.push({
          label: cityNames[citySlug],
          href: location.pathname,
          isActive: true
        });
      }
    }
    
    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();
  
  // Generate JSON-LD schema for breadcrumbs
  const generateBreadcrumbSchema = () => {
    const baseUrl = window.location.origin;
    
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.label,
        "item": `${baseUrl}${item.href}`
      }))
    };
  };

  // Inject schema
  React.useEffect(() => {
    const existingSchema = document.querySelector('script[data-type="breadcrumb-schema"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-type', 'breadcrumb-schema');
    script.textContent = JSON.stringify(generateBreadcrumbSchema());
    document.head.appendChild(script);

    return () => {
      const schemaScript = document.querySelector('script[data-type="breadcrumb-schema"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [location.pathname, currentLanguage]);

  return (
    <nav 
      aria-label="Breadcrumb" 
      className="py-4 bg-card/50 border-b border-border/50"
    >
      <div className="container mx-auto px-4">
        <ol className="flex items-center space-x-2 text-sm">
          {breadcrumbs.map((item, index) => (
            <li key={index} className="flex items-center">
              {index > 0 && (
                <ChevronRight className="w-4 h-4 text-muted-foreground mx-2" />
              )}
              
              {index === 0 && (
                <Home className="w-4 h-4 mr-2 text-muted-foreground" />
              )}
              
              {item.isActive ? (
                <span className="text-foreground font-medium">
                  {item.label}
                </span>
              ) : (
                <Link 
                  to={item.href}
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  {item.label}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
};

export default EUBreadcrumbs;